import type { RouteRecordRaw } from 'vue-router'

import GenericLandingPage from './views/GenericLandingPage.vue'
import { defaultLandingPage, landingPages } from './content/landingPages'

const VideoWatermarkRemovalPage = () => import('./views/VideoWatermarkRemovalPage.vue')
const VideoObjectRemovalPage = () => import('./views/VideoObjectRemovalPage.vue')
const VideoSubtitleRemovalPage = () => import('./views/VideoSubtitleRemovalPage.vue')
const VideoUpscalePage = () => import('./views/VideoUpscalePage.vue')
const ImageObjectRemovalPage = () => import('./views/ImageObjectRemovalPage.vue')
const VideoSubtitleRemovalEmbedPage = () => import('./views/VideoSubtitleRemovalEmbedPage.vue')

const WorkbenchLayout = () => import('./workbench/WorkbenchLayout.vue')
const AiToolsPage = () => import('./workbench/pages/AiToolsPage.vue')
const BatchWorkspacePage = () => import('./workbench/pages/BatchWorkspacePage.vue')
const TaskListPage = () => import('./workbench/pages/TaskListPage.vue')
const RechargePage = () => import('./workbench/pages/RechargePage.vue')
const ContactPage = () => import('./workbench/pages/ContactPage.vue')
const AccountPage = () => import('./workbench/pages/AccountPage.vue')
const SubAccountManagePage = () => import('./workbench/pages/SubAccountManagePage.vue')
const AffiliateManagePage = () => import('./workbench/pages/AffiliateManagePage.vue')
const WorkbenchIndexRedirectPage = () => import('./workbench/pages/WorkbenchIndexRedirectPage.vue')

const baseUrl = (import.meta.env.BASE_URL as string | undefined) || '/'
const isSubdirDeploy = baseUrl !== '/'
const buildTarget = String((import.meta.env as any)?.VITE_BUILD_TARGET || '').trim()
const isWorkbenchOnly = buildTarget === 'workbench'
const landingOnlyId = buildTarget.startsWith('landing-') ? buildTarget.replace(/^landing-/, '') : ''

function pageProps(p: (typeof landingPages)[number]) {
  const out: any = {
    landingPageId: p.id,
    landingSeo: p.landingSeo,
    canonicalPath: p.canonicalPath,
    breadcrumbName: p.breadcrumbName,
    howToTitle: p.howToTitle,
    seoTitle: p.title,
    seoDescription: p.description,
    benefitsImages: p.benefitsImages,
    featuresImages: p.featuresImages,
    whyChooseImages: p.whyChooseImages,
    enabledSections: p.enabledSections,
    demoVideoUrl: p.demoVideoUrl,
    defaultInvitationCode: p.defaultInvitationCode,
    recommendations: p.recommendations,
    hreflang: p.hreflang,
  }
  if (p.useCasesImages?.length) out.useCasesImages = p.useCasesImages
  return out
}

const landingRoutes: RouteRecordRaw[] = landingPages.map((p) => ({
  path: p.routePath,
  name: p.id,
  component:
    p.id === 'video-object-removal' || p.id === 'doubao-watermark-removal' || p.id === 'jimeng-watermark-removal'
      ? VideoObjectRemovalPage
      : p.id === 'image-object-removal' || p.id === 'photo-watermark-removal'
        ? ImageObjectRemovalPage
      : p.id === 'video-enhance'
        ? VideoUpscalePage
      : p.id === 'video-subtitle-removal' || p.id === 'bilibili-watermark-removal' || p.id === 'rednote-watermark-removal' || p.id === 'douyin-watermark-removal' || p.id === 'keling-watermark-removal'
        ? VideoSubtitleRemovalPage
      : p.id === 'video-watermark-removal' 
        ? VideoWatermarkRemovalPage
        : GenericLandingPage,
  props: pageProps(p),
}))

function landingComponentForId(id: string) {
  return id === 'video-object-removal' || id === 'doubao-watermark-removal' || id === 'jimeng-watermark-removal'
    ? VideoObjectRemovalPage
    : id === 'image-object-removal' || id === 'photo-watermark-removal'
      ? ImageObjectRemovalPage
      : id === 'video-enhance' || id === 'ai-video-enhancer'
        ? VideoUpscalePage
      : id === 'video-subtitle-removal'
        ? VideoSubtitleRemovalPage
      : id === 'video-watermark-removal' || id === 'bilibili-watermark-removal'
        ? VideoWatermarkRemovalPage
        : GenericLandingPage
}

const workbenchRoute: RouteRecordRaw = {
  path: isWorkbenchOnly ? '/' : '/ai-workbench/',
  component: WorkbenchLayout,
  children: [
    { path: '', name: 'wb-home', component: WorkbenchIndexRedirectPage },
    { path: 'tools/', name: 'wb-tools', component: AiToolsPage, meta: { title: 'AI Smart Tools', subtitle: '' } },
    {
      path: 'batch/',
      name: 'wb-batch',
      component: BatchWorkspacePage,
      meta: { title: 'Batch Processing', subtitle: 'Process multiple assets with unified parameters.' },
    },
    { path: 'tasks/', name: 'wb-tasks', component: TaskListPage, meta: { title: 'Task List', subtitle: 'Search, filter, and manage recent tasks.' } },
    {
      path: 'recharge/',
      name: 'wb-recharge',
      component: RechargePage,
      meta: { title: 'Recharge & Consumption', subtitle: 'View your recharge and usage history.' },
    },
    { path: 'subaccounts/', name: 'wb-subaccounts', component: SubAccountManagePage, meta: { title: 'Sub Accounts', subtitle: '' } },
    { path: 'affiliate/', name: 'wb-affiliate', component: AffiliateManagePage, meta: { title: 'Affiliate', subtitle: '' } },
    { path: 'contact/', name: 'wb-contact', component: ContactPage, meta: { title: 'Contact Us', subtitle: 'Scan QR code to reach support.' } },
    { path: 'account/', name: 'wb-account', component: AccountPage, meta: { title: 'Account Settings', subtitle: 'Manage profile and security settings.' } },
  ],
}

const embedRoutes: RouteRecordRaw[] = [
  { path: '/embed/video-subtitle-removal/', name: 'embed-video-subtitle-removal', component: VideoSubtitleRemovalEmbedPage },
]

export const routes: RouteRecordRaw[] = isWorkbenchOnly
  ? [workbenchRoute, { path: '/:pathMatch(.*)*', redirect: '/' }]
  : landingOnlyId
    ? (() => {
        const landing = landingPages.find((p) => p.id === landingOnlyId) || defaultLandingPage
        return [
          { path: '/', name: landing.id, component: landingComponentForId(landing.id), props: pageProps(landing) },
          { path: '/:pathMatch(.*)*', redirect: '/' },
        ].filter(Boolean) as RouteRecordRaw[]
      })()
    : isSubdirDeploy
      ? [
          { path: '/', name: defaultLandingPage.id, component: VideoWatermarkRemovalPage, props: pageProps(defaultLandingPage) },
          workbenchRoute,
          ...embedRoutes,
          ...landingRoutes,
          { path: '/:pathMatch(.*)*', redirect: '/' },
        ]
      : [
          { path: '/', redirect: defaultLandingPage.routePath },
          workbenchRoute,
          ...embedRoutes,
          ...landingRoutes,
          { path: '/:pathMatch(.*)*', redirect: defaultLandingPage.routePath },
        ]
